// Purchasereport.jsx — Trial Balance styled header + Sales Report styled table
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import "./TrialBalance.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Helper to extract currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }
  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }
  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }
  return currency;
};

// ✅ Searchable dropdown
const SearchableDropdown = ({ options, value, onChange, placeholder, label }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  const selectedOption = options.find(opt => opt.value === value);
  const displayValue = selectedOption ? selectedOption.label : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (selected) => {
    onChange(selected);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <div className="filter-group" style={{ position: 'relative' }} ref={wrapperRef}>
      <label>{label}</label>

      <div className="searchable-wrapper">
        <input
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
        />
        <span className="searchable-arrow" onClick={() => setIsOpen(!isOpen)}>▼</span>
      </div>

      {isOpen && (
        <div className="searchable-menu">
          {filteredOptions.length === 0 ? (
            <div className="searchable-item searchable-empty">No options</div>
          ) : (
            filteredOptions.map(opt => (
              <div
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`searchable-item ${opt.value === value ? 'searchable-item-selected' : ''}`}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

const Purchasereport = () => {
  const [purchases, setPurchases] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [loadingCompany, setLoadingCompany] = useState(true);

  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [batchNumbers, setBatchNumbers] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(true);

  // Filters
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedBatchNo, setSelectedBatchNo] = useState('');

  const printRef = useRef();

  const formatCurrency = useCallback((amount) => {
    let code = getCompanyCurrency(company);

    if (code === "USD") {
      const first = filteredData[0] || purchases[0];
      if (first) {
        const fromRow =
          (typeof first.currency === "string" && /^[A-Z]{3}$/.test(first.currency) && first.currency) ||
          (typeof first.currency_code === "string" && /^[A-Z]{3}$/.test(first.currency_code) && first.currency_code) ||
          (first.company_id && getCompanyCurrency(first.company_id)) ||
          null;
        if (fromRow && fromRow !== "USD") code = fromRow;
      }
    }

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${code} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [company, filteredData, purchases]);

  const fetchCompany = useCallback(async () => {
    try {
      setLoadingCompany(true);
      const res = await axios.get(`${url}/api/company/current`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      const payload = res?.data?.data || res?.data || null;
      setCompany(payload);
    } catch (err) {
      setCompany(null);
    } finally {
      setLoadingCompany(false);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const res = await axios.get(`${url}/api/products/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      if (res.data.success && Array.isArray(res.data.data)) {
        setProducts(res.data.data);
      } else {
        setProducts([]);
      }
    } catch (err) {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      setLoadingSuppliers(true);
      const res = await axios.get(`${url}/api/supplier/dropdown`, {
        headers: getAuthHeaders(),
        timeout: 5000,
      });
      if (res.data.success && Array.isArray(res.data.data)) {
        setSuppliers(res.data.data);
      } else {
        setSuppliers([]);
      }
    } catch (err) {
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (fromDate) params.start_date = fromDate;
      if (toDate) params.end_date = toDate;
      if (selectedProductId) params.product_id = selectedProductId;
      if (selectedSupplierId) params.supplier_id = selectedSupplierId;
      params.limit = 1000;

      const res = await axios.get(`${url}/api/purchase`, {
        headers: getAuthHeaders(),
        params,
      });
      const data = res.data.data || [];
      const purchaseTxns = data.filter(tx => !tx.is_return);

      const batches = new Set();
      purchaseTxns.forEach(item => {
        if (item.batch_number) batches.add(item.batch_number);
      });
      setBatchNumbers(Array.from(batches).sort());

      let filtered = purchaseTxns;
      if (selectedBatchNo) {
        filtered = filtered.filter(item => item.batch_number === selectedBatchNo);
      }

      setPurchases(purchaseTxns);
      setFilteredData(filtered);

      if (filtered.length === 0 && (selectedBatchNo || purchaseTxns.length > 0)) {
        setError(
          selectedBatchNo
            ? `No purchase records found for batch "${selectedBatchNo}".`
            : 'No purchase records found for the selected filters.'
        );
      } else {
        setError(null);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load purchase report');
      setPurchases([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, selectedProductId, selectedSupplierId, selectedBatchNo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 300);
    return () => clearTimeout(timer);
  }, [fromDate, toDate, selectedProductId, selectedSupplierId, selectedBatchNo, fetchReport]);

  useEffect(() => {
    fetchCompany();
    fetchProducts();
    fetchSuppliers();
  }, [fetchCompany, fetchProducts, fetchSuppliers]);

  useEffect(() => {
    if (!loadingCompany && !loadingProducts && !loadingSuppliers) {
      fetchReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCompany, loadingProducts, loadingSuppliers]);

  const handleGenerate = () => fetchReport();

  const companyAddress = company?.admin_user?.Usercomp_address || company?.address || '';
  const companyContact = company?.admin_user?.Usercomp_contact || company?.contact || '';
  const companyContact2 = company?.admin_user?.Usercomp_contact_2 || company?.contact_2 || '';
  const companyContact3 = company?.admin_user?.Usercomp_contact_3 || company?.contact_3 || '';
  const companyEmail = company?.admin_user?.Usercomp_email || company?.admin_email || '';

  // ✅ Phone 1 · Phone 2 · Phone 3
  const phoneLine = [companyContact, companyContact2, companyContact3]
    .filter(Boolean)
    .join(' · ');

  const getLogoUrl = () => {
    if (!company?.logo) return null;
    if (company.logo.startsWith('http')) return company.logo;
    const cleanPath = company.logo.replace(/^\/+/, '');
    return `${url}/${cleanPath}`;
  };

  // ✅ Single print handler — uses the browser's native print with the
  //    .print-only block below.
  const handlePrint = () => window.print();

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : '-');

  const primaryColor = '#2e3192';

  const totalTax = filteredData.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const totalDiscount = filteredData.reduce(
    (sum, item) => sum + (item.discount_amount || 0) + (item.manual_discount || 0),
    0
  );
  const totalShippingOther = filteredData.reduce(
    (sum, item) => sum + (item.shipping_cost || 0) + (item.other_charges || 0),
    0
  );
  const grandTotal = filteredData.reduce((sum, item) => sum + (item.grand_total || 0), 0);

  const productOptions = products.map(p => ({
    value: p._id,
    label: `${p.product_code} - ${p.product_name}`,
  }));
  const supplierOptions = suppliers.map(s => ({ value: s._id, label: s.supplier_name }));
  const batchOptions = batchNumbers.map(b => ({ value: b, label: b }));

  if (loadingCompany && !company) {
    return (
      <div className="trial-balance-page">
        <div className="trial-balance-container">
          <div className="loading-container">Loading company data…</div>
        </div>
      </div>
    );
  }

  const logoUrl = getLogoUrl();
  const hasRecords = filteredData.length > 0;
  const statusLabel = loading ? 'Loading…' : hasRecords ? 'Loaded' : 'Empty';

  const formattedAsOfDate = toDate
    ? new Date(`${toDate}T00:00:00`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    : new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const fiscalYearLabel = company?.fiscal_year_start
    ? new Date(company.fiscal_year_start).getFullYear()
    : new Date().getFullYear();

  const companyCurrency = getCompanyCurrency(company);

  const rangeLabel = (() => {
    if (fromDate && toDate) {
      return `${new Date(fromDate).toLocaleDateString()} - ${new Date(toDate).toLocaleDateString()}`;
    }
    if (fromDate) return `From ${new Date(fromDate).toLocaleDateString()}`;
    if (toDate) return `Until ${new Date(toDate).toLocaleDateString()}`;
    return 'All Time';
  })();

  return (
    <div className="trial-balance-page">
      {/* ==================================================
          SCREEN PREVIEW
      ================================================== */}
      <div className="screen-only trial-balance-container" ref={printRef}>
        {/* PAGE HEADER */}
        <div className="report-header">
          <div>
            <p className="report-eyebrow">PURCHASE REPORTS</p>
            <h1 className="report-title">Purchase Report</h1>
            <p className="report-subtitle">
              Review purchase transactions and their tax, discount, and shipping
              breakdown for the selected date range.
            </p>

            {(logoUrl || company?.company_name) && (
              <div className="purchase-company-strip">
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div>
                  <div style={{ fontWeight: 700, color: '#1e2a5a', fontSize: '0.95rem', lineHeight: 1.15 }}>
                    {company?.company_name || 'Company Name'}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#6c757d', lineHeight: 1.2, marginTop: 2 }}>
                    {company?.business_type?.name || 'Business'} | Code: {company?.company_code || 'N/A'}
                  </div>
                  {(companyAddress || phoneLine || companyEmail) && (
                    <div style={{ fontSize: '0.72rem', color: '#6c757d', lineHeight: 1.4, marginTop: 4 }}>
                      {companyAddress && <span>{companyAddress}</span>}
                      {companyAddress && (phoneLine || companyEmail) && <span> · </span>}
                      {phoneLine && <span>📞 {phoneLine}</span>}
                      {phoneLine && companyEmail && <span> · </span>}
                      {companyEmail && <span>✉️ {companyEmail}</span>}
                      {company?.country?.name && <span> · {company.country.name}</span>}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={`balance-status no-print ${hasRecords ? 'status-balanced' : 'status-unbalanced'}`}>
            <span className="status-dot" />
            <div>
              <span className="status-label">Status</span>
              <strong>{statusLabel}</strong>
            </div>
          </div>
        </div>

        {/* FILTER */}
        <div className="filter-card no-print">
          <div className="filter-fields">
            <div className="filter-group">
              <label htmlFor="from-date">From Date</label>
              <input
                id="from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="to-date">To Date</label>
              <input
                id="to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <SearchableDropdown
              options={productOptions}
              value={selectedProductId}
              onChange={setSelectedProductId}
              placeholder="Search product..."
              label="Product"
            />

            <SearchableDropdown
              options={supplierOptions}
              value={selectedSupplierId}
              onChange={setSelectedSupplierId}
              placeholder="Search supplier..."
              label="Supplier"
            />

            <SearchableDropdown
              options={batchOptions}
              value={selectedBatchNo}
              onChange={setSelectedBatchNo}
              placeholder="Search batch..."
              label="Batch No"
            />

            <div className="filter-actions">
              <button
                type="button"
                className="refresh-button"
                onClick={handleGenerate}
                disabled={loading}
              >
                <span className={loading ? 'refresh-icon spinning' : 'refresh-icon'}>↻</span>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>

              <button
                type="button"
                className="refresh-button print-button"
                onClick={handlePrint}
                disabled={filteredData.length === 0}
              >
                🖨️ Print
              </button>
            </div>
          </div>

          <div className="records-count">
            <span className="records-icon">▤</span>
            <span>
              Showing <strong>{filteredData.length}</strong>{' '}
              {filteredData.length === 1 ? 'record' : 'records'}
            </span>
          </div>
        </div>

        {/* SUMMARY CARDS */}
        <div className="summary-grid no-print">
          <div className="summary-card debit-summary">
            <div className="summary-icon">TX</div>
            <div>
              <span className="summary-label">Total Tax</span>
              <h3>{formatCurrency(totalTax)}</h3>
            </div>
          </div>

          <div className="summary-card credit-summary">
            <div className="summary-icon">DC</div>
            <div>
              <span className="summary-label">Total Discount</span>
              <h3>{formatCurrency(totalDiscount)}</h3>
            </div>
          </div>

          <div className="summary-card accounts-summary">
            <div className="summary-icon">SH</div>
            <div>
              <span className="summary-label">Shipping + Other</span>
              <h3>{formatCurrency(totalShippingOther)}</h3>
            </div>
          </div>

          <div className={`summary-card ${hasRecords ? 'balance-summary' : 'difference-summary'}`}>
            <div className="summary-icon">{hasRecords ? '✓' : '!'}</div>
            <div>
              <span className="summary-label">
                {hasRecords ? 'Grand Total' : 'No Records'}
              </span>
              <h3>{hasRecords ? formatCurrency(grandTotal) : '—'}</h3>
            </div>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <div className="error-icon">!</div>
            <div>
              <strong>Unable to load report</strong>
              <span>{error}</span>
            </div>
            <button type="button" onClick={handleGenerate}>
              Try Again
            </button>
          </div>
        )}

        {/* TABLE */}
        <div className="table-card">
          <div className="table-card-header">
            <div>
              <h2>Purchase Transactions</h2>
              <p>Tax, discount, shipping and totals for each purchase line.</p>
            </div>
            <span className="table-count">
              {filteredData.length} {filteredData.length === 1 ? 'Record' : 'Records'}
            </span>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: primaryColor, color: 'white' }}>
                  <tr>
                    <th style={{ padding: '0.75rem' }}>No.</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Invoice No</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Supplier</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Item Name</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Batch No</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Qty</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Unit Price</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tax</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Discount</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Shipping + Other</th>
                    <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.length === 0 && !loading && (
                    <tr>
                      <td colSpan="12" style={{ textAlign: 'center', padding: '2rem' }}>
                        No purchase records found. Click Refresh.
                      </td>
                    </tr>
                  )}

                  {filteredData.map((item, idx) => {
                    const product = item.product_id || {};
                    const supplier = item.supplier_id || {};
                    const tax = item.tax_amount || 0;
                    const combinedDiscount = (item.discount_amount || 0) + (item.manual_discount || 0);
                    const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
                    const totalAmount = item.grand_total || 0;

                    return (
                      <tr key={item._id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>{idx + 1}</td>
                        <td style={{ padding: '0.75rem' }}>{item.invoice_no || item.reference_id || '—'}</td>
                        <td style={{ padding: '0.75rem' }}>{formatDate(item.transaction_date)}</td>
                        <td style={{ padding: '0.75rem' }}>{supplier.supplier_name || 'Unknown'}</td>
                        <td style={{ padding: '0.75rem' }}>{product.product_name || 'Unknown'}</td>
                        <td style={{ padding: '0.75rem' }}>{item.batch_number || '—'}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{Math.abs(item.quantity || 0)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(item.unit_cost)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(tax)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(combinedDiscount)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(shippingOther)}</td>
                        <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(totalAmount)}</td>
                      </tr>
                    );
                  })}

                  {filteredData.length > 0 && (
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="7" style={{ padding: '0.75rem', textAlign: 'right' }}>
                        Grand Totals:
                      </td>
                      <td></td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalTax)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalDiscount)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(totalShippingOther)}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'right' }}>{formatCurrency(grandTotal)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="hide-on-print" style={{ fontSize: '0.72rem', textAlign: 'center', marginTop: '1rem', color: '#adb5bd' }}>
          Generated on {new Date().toLocaleString()}
        </div>
      </div>

      {/* ==================================================
          PRINT-ONLY A4 LANDSCAPE REPORT
      ================================================== */}
      <div className="print-only print-page">
        {/* Company header */}
        <div className="print-header">
          <div className="print-header-left">
            {logoUrl && (
              <img
                src={logoUrl}
                alt="Logo"
                className="print-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="print-header-text">
              <h1 className="print-company-name">
                {company?.company_name || 'Company Name'}
              </h1>
              <p className="print-company-line">
                {companyAddress || 'No address'}
              </p>
              {phoneLine && (
                <p className="print-company-line">Tel: {phoneLine}</p>
              )}
              <p className="print-company-line">
                {companyEmail ? `Email: ${companyEmail}` : ''}
                {company?.company_code ? ` · Code: ${company.company_code}` : ''}
              </p>
            </div>
          </div>
          <div className="print-header-right">
            <div className="print-meta-item">
              <span className="print-meta-label">Report Date</span>
              <span className="print-meta-value">{formattedAsOfDate}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Fiscal Year</span>
              <span className="print-meta-value">{fiscalYearLabel}-{fiscalYearLabel + 1}</span>
            </div>
            <div className="print-meta-item">
              <span className="print-meta-label">Currency</span>
              <span className="print-meta-value">{companyCurrency}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="print-title-block">
          <h2 className="print-report-title">PURCHASE REPORT</h2>
          <p className="print-report-subtitle">
            {rangeLabel} · {filteredData.length} record(s)
          </p>
        </div>

        {/* Print table */}
        <table className="print-table">
          <thead>
            <tr>
              <th className="print-col-no">No.</th>
              <th>Invoice No</th>
              <th>Date</th>
              <th>Supplier</th>
              <th>Item Name</th>
              <th>Batch No</th>
              <th className="print-col-qty">Qty</th>
              <th className="print-col-money">Unit Price</th>
              <th className="print-col-money">Tax</th>
              <th className="print-col-money">Discount</th>
              <th className="print-col-money">Shipping+Other</th>
              <th className="print-col-money">Total</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="12" className="print-empty">
                  No purchase records found for the selected filters.
                </td>
              </tr>
            ) : (
              filteredData.map((item, idx) => {
                const product = item.product_id || {};
                const supplier = item.supplier_id || {};
                const tax = item.tax_amount || 0;
                const combinedDiscount = (item.discount_amount || 0) + (item.manual_discount || 0);
                const shippingOther = (item.shipping_cost || 0) + (item.other_charges || 0);
                const totalAmount = item.grand_total || 0;
                return (
                  <tr key={item._id || `p-${idx}`}>
                    <td className="print-col-no">{idx + 1}</td>
                    <td>{item.invoice_no || item.reference_id || '—'}</td>
                    <td>{formatDate(item.transaction_date)}</td>
                    <td>{supplier.supplier_name || 'Unknown'}</td>
                    <td>{product.product_name || 'Unknown'}</td>
                    <td>{item.batch_number || '—'}</td>
                    <td className="print-col-qty">{Math.abs(item.quantity || 0)}</td>
                    <td className="print-col-money">{formatCurrency(item.unit_cost)}</td>
                    <td className="print-col-money">{formatCurrency(tax)}</td>
                    <td className="print-col-money">{formatCurrency(combinedDiscount)}</td>
                    <td className="print-col-money">{formatCurrency(shippingOther)}</td>
                    <td className="print-col-money print-total-value">{formatCurrency(totalAmount)}</td>
                  </tr>
                );
              })
            )}
            {filteredData.length > 0 && (
              <tr className="print-total-row">
                <td colSpan="8" className="print-total-label">GRAND TOTAL</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalTax)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalDiscount)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(totalShippingOther)}</td>
                <td className="print-col-money print-total-value">{formatCurrency(grandTotal)}</td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Signature footer */}
        <div className="print-footer">
          <div className="print-footer-row">
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Prepared By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Checked By</span>
            </div>
            <div className="print-signature">
              <div className="print-signature-line" />
              <span>Approved By</span>
            </div>
          </div>
          <p className="print-footer-note">
            Generated on {new Date().toLocaleString()} · HantiZone IMS
          </p>
        </div>
      </div>

      <style>{`
        .print-only { display: none; }

        @media print {
          .no-print { display: none !important; }
          .hide-on-print { display: none !important; }
        }

        @page {
          size: A4 landscape;
          margin: 10mm 12mm 10mm 12mm;
        }

        @media print {
          html, body {
            background: #fff !important;
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-size: 10pt;
          }

          /* Hide EVERYTHING (sidebar, menu, search, filters, summary cards) */
          body * { visibility: hidden !important; }

          /* Show only the print block */
          .print-only,
          .print-only * { visibility: visible !important; }

          .print-only {
            position: absolute !important;
            left: 0; top: 0;
            width: 100%;
            display: block !important;
            padding: 0; margin: 0;
            background: #fff !important;
          }

          .screen-only,
          .no-print,
          .hide-on-print { display: none !important; }

          /* ---------- PRINT BLOCK LAYOUT ---------- */
          .print-page {
            font-family: "Helvetica Neue", Arial, sans-serif;
            color: #111;
          }

          .print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 10mm;
            padding-bottom: 3mm;
            border-bottom: 1.5pt solid #222;
            margin-bottom: 4mm;
          }
          .print-header-left { display: flex; align-items: center; gap: 6mm; flex: 1; }
          .print-logo { max-height: 16mm; max-width: 24mm; object-fit: contain; }
          .print-company-name { font-size: 13pt; font-weight: 700; color: #170f48; margin: 0 0 0.8mm 0; }
          .print-company-line { font-size: 8pt; color: #444; margin: 0.3mm 0; }
          .print-header-right { text-align: right; font-size: 8pt; }
          .print-meta-item { display: flex; justify-content: flex-end; gap: 4mm; margin-bottom: 0.8mm; }
          .print-meta-label { color: #666; }
          .print-meta-value { font-weight: 700; min-width: 24mm; text-align: right; color: #111; }

          .print-title-block { text-align: center; margin-bottom: 4mm; }
          .print-report-title { font-size: 13pt; font-weight: 700; letter-spacing: 2pt; color: #170f48; margin: 0 0 0.8mm 0; }
          .print-report-subtitle { font-size: 9pt; color: #555; margin: 0; }

          /* ---------- PRINT TABLE ---------- */
          .print-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
            page-break-inside: auto;
          }
          .print-table thead { display: table-header-group; }
          .print-table tr { page-break-inside: avoid; }
          .print-table th,
          .print-table td {
            border: 0.5pt solid #999;
            padding: 2.5pt 3.5pt;
            vertical-align: middle;
          }
          .print-table thead th {
            background: #eeeeee !important;
            color: #000 !important;
            font-weight: 700;
            text-align: left;
            font-size: 7.5pt;
            text-transform: uppercase;
            letter-spacing: 0.2pt;
          }
          .print-col-no    { width: 8mm; text-align: center; }
          .print-col-qty   { width: 14mm; text-align: right; }
          .print-col-money {
            width: 20mm;
            text-align: right;
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
          }

          .print-total-row { background: #f4f4f4 !important; font-weight: 700; }
          .print-total-label { text-align: right; letter-spacing: 0.5pt; }
          .print-total-value { font-weight: 700; }
          .print-empty { text-align: center; padding: 12pt; font-style: italic; }

          /* ---------- PRINT FOOTER ---------- */
          .print-footer { margin-top: 8mm; page-break-inside: avoid; }
          .print-footer-row {
            display: flex;
            justify-content: space-between;
            gap: 8mm;
            margin-bottom: 4mm;
          }
          .print-signature { flex: 1; text-align: center; font-size: 8pt; color: #333; }
          .print-signature-line { border-top: 0.5pt solid #333; margin-bottom: 1.5mm; height: 10mm; }
          .print-footer-note { text-align: center; font-size: 7pt; color: #888; margin: 0; }
        }

        /* ============================================================
           Company strip (screen)
           ============================================================ */
        .purchase-company-strip {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-top: 6px;
        }
        .purchase-company-strip img {
          height: 34px;
          width: 34px;
          object-fit: contain;
          border-radius: 6px;
          border: 1px solid #e9ecef;
        }

        /* ============================================================
           Searchable dropdown
           ============================================================ */
        .searchable-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .searchable-wrapper input {
          width: 100%;
          height: 34px !important;
          padding: 0 26px 0 10px !important;
          font-size: 12.5px !important;
          border-radius: 6px !important;
        }
        .searchable-arrow {
          position: absolute;
          right: 10px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 0.7rem;
          color: #868e96;
          cursor: pointer;
        }
        .searchable-menu {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          max-height: 220px;
          overflow-y: auto;
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 8px;
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
          z-index: 1000;
        }
        .searchable-item {
          padding: 8px 12px;
          font-size: 0.85rem;
          cursor: pointer;
          border-bottom: 1px solid #f1f3f5;
          color: #212529;
        }
        .searchable-item:last-child { border-bottom: none; }
        .searchable-item:hover { background: #f8f9fa; }
        .searchable-item-selected { background: #eef2ff; }
        .searchable-empty { color: #868e96; cursor: default; font-style: italic; }

        /* ============================================================
           Filter card
           ============================================================ */
        .filter-card {
          padding: 12px 14px !important;
          margin-bottom: 12px !important;
          gap: 12px !important;
          align-items: center !important;
        }
        .filter-fields { gap: 10px !important; }
        .filter-group { gap: 4px !important; }
        .filter-group label {
          font-size: 10.5px !important;
          font-weight: 700 !important;
          color: #475569 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.4px !important;
          margin: 0 !important;
        }
        .filter-group input,
        .filter-group select {
          height: 34px !important;
          min-width: 0 !important;
          padding: 0 10px !important;
          font-size: 12.5px !important;
          border-radius: 6px !important;
        }
        .filter-group > input[type="date"] { min-width: 140px !important; }
        .filter-group .searchable-wrapper { min-width: 150px !important; }

        .filter-actions {
          display: flex !important;
          gap: 6px !important;
          align-items: flex-end !important;
        }
        .refresh-button {
          height: 34px !important;
          padding: 0 14px !important;
          font-size: 12px !important;
          font-weight: 600 !important;
          border-radius: 6px !important;
          background: #ffffff !important;
          color: #1e2a5a !important;
          border: 1px solid #dee2e6 !important;
          white-space: nowrap;
          cursor: pointer;
          transition: background 0.15s ease, border-color 0.15s ease;
        }

        .refresh-button:not(:disabled):hover {
          background: #f1f5f9 !important;
          border-color: #cbd5e1 !important;
        }

        .refresh-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .refresh-icon { font-size: 15px !important; }
        .print-button {
          background: #ffffff !important;
          color: #1e2a5a !important;
          border: 1px solid #dee2e6 !important;
          white-space: nowrap;
        }
        .print-button:not(:disabled):hover { background: #f8f9fa !important; }
        .print-button:disabled { opacity: 0.5; cursor: not-allowed; }

        .records-count { font-size: 11.5px !important; min-height: 34px !important; }

        /* ============================================================
           Summary cards
           ============================================================ */
        .summary-grid { gap: 10px !important; margin-bottom: 12px !important; }
        .summary-card {
          min-height: 62px !important;
          padding: 10px 14px !important;
          gap: 10px !important;
          border-radius: 10px !important;
        }
        .summary-icon {
          width: 34px !important;
          height: 34px !important;
          font-size: 11px !important;
          border-radius: 8px !important;
        }
        .summary-label { font-size: 10px !important; margin-bottom: 2px !important; }
        .summary-card h3 { font-size: 16px !important; }

        /* ============================================================
           Table card header + footer
           ============================================================ */
        .table-card-header { padding: 12px 16px !important; }
        .table-card-header h2 { font-size: 14px !important; }
        .table-card-header p { font-size: 11.5px !important; }
        .table-count { font-size: 10.5px !important; padding: 4px 8px !important; }

        .table-footer {
          display: flex !important;
          align-items: center !important;
          gap: 24px !important;
          padding: 10px 18px !important;
          background: #fbfcfe !important;
          border-top: 1px solid #e2e8f0 !important;
          flex-wrap: wrap !important;
        }
        .table-footer .footer-detail {
          display: flex !important;
          flex-direction: column !important;
          gap: 3px !important;
          align-items: flex-start !important;
        }
        .table-footer .footer-detail span {
          display: block !important;
          font-size: 9px !important;
          font-weight: 700 !important;
          color: #94a3b8 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.6px !important;
          line-height: 1 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .table-footer .footer-detail strong {
          display: block !important;
          font-size: 11.5px !important;
          color: #334155 !important;
          font-weight: 600 !important;
          line-height: 1.2 !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .table-footer .footer-divider {
          width: 1px !important;
          height: 26px !important;
          background: #e2e8f0 !important;
          flex-shrink: 0 !important;
        }
        .table-footer .footer-status {
          margin-left: auto !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 5px 10px !important;
          border-radius: 999px !important;
          font-size: 10.5px !important;
          font-weight: 700 !important;
          background: #eaf8f0 !important;
          color: #168346 !important;
        }
        .table-footer .footer-status span {
          width: 15px !important;
          height: 15px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 50% !important;
          background: #168346 !important;
          color: #ffffff !important;
          font-size: 9px !important;
        }

        /* ============================================================
           Page level
           ============================================================ */
        .trial-balance-page { padding: 18px !important; }
        .report-header { margin-bottom: 14px !important; }
        .report-title { font-size: 22px !important; }
        .report-subtitle { font-size: 12px !important; margin-top: 3px !important; }
        .report-eyebrow { font-size: 10px !important; margin-bottom: 2px !important; }
      `}</style>
    </div>
  );
};

export default Purchasereport;
